import React, { Dispatch, SetStateAction, useEffect, useState, useRef } from 'react';
import { RouteComponentProps } from '@reach/router';
import WondeImg from '../../images/Wonde_Logo.png';
import ProgressIndicator from '../../images/Progress_Indicator.gif';
import BlueTick from '../../images/Blue_Tick.png';
import { WondeApi } from 'api/wondeApi';
import { SyncWondeDataResponse } from './models';
import { MFA_REQUIRED_CODE, hasMessageCode } from 'pages/mfa/models';

export type wondeAccessDetails = {
  accessRequested: boolean;
  accessApproved: boolean;
  wondeSchoolId: string | null;
  schoolName: string | null;
  schoolAddress: string | null;
  schoolUsersEmailAddress: string | null;
};

type wondeENSchoolsList = {
  id: string;
  name: string;
  address: wondeENSchoolAddress;
};

type wondeENSchoolAddress = {
  address_line_1: string | null;
  address_line_2: string | null;
  address_town: string | null;
  address_postcode: string | null;
  address_country: wondeENSchoolAddressCountry;
};

type wondeENSchoolAddressCountry = {
  code: string | null;
  name: string | null;
};

type WondeLinkProps = {
  setWondeIntegration: Dispatch<SetStateAction<boolean>>;
  selectedSyncType: string;
  setSelectedSyncType: Dispatch<SetStateAction<string>>;
  wondeIntegration: boolean;
  setWondeSchoolId: any;
  setSyncResponse: Dispatch<React.SetStateAction<SyncWondeDataResponse | null>>
  wondeApprovalGuide: string | null;
  schoolURN: string | null | undefined;
};

type Props = WondeLinkProps & RouteComponentProps;

export const WondeLink: React.FC<Props> = ({ 
  setWondeIntegration,
  selectedSyncType,
  setSelectedSyncType,
  wondeIntegration,
  setWondeSchoolId,
  setSyncResponse,
  wondeApprovalGuide = "",
  schoolURN,
}) => {
  const [linkStatusLoading, setLinkStatusLoading] = useState<boolean>(true);
  const [linkStatusLoadingError, setLinkStatusLoadingError] = useState<boolean>(false);
  const [wondeAccessDetails, setWondeAccessDetails] = useState<wondeAccessDetails>({
    accessRequested: false,
    accessApproved: false,
    wondeSchoolId: null,
    schoolName: null,
    schoolAddress: null,
    schoolUsersEmailAddress: null,
  });
  const [educationNumber, setEducationNumber] = useState<string>("");
  const [initEduNumberSet, setInitEduNumberSet] = useState<boolean>(false);
  const [findSchoolLoading, setFindSchoolLoading] = useState<boolean>(false);
  const [findSchoolLoadingError, setFindSchoolLoadingError] = useState<string | null>(null);
  const [syncLoadingError, setSyncLoadingError] = useState<string | null>(null);  
  const [schoolsList, setSchoolsList] = useState<wondeENSchoolsList[]>([]);
  const [selectSchoolLoading, setSelectSchoolLoading] = useState<boolean>(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [selectedSchoolError, setSelectedSchoolError] = useState<string | null>(null);

  const savedCallback = useRef<any>();

  useEffect(() => {
    checkLinkStatus();
  }, []);

  useEffect(() => {
    function wondeTimer() {
      savedCallback.current();
    }

    let id = setInterval(wondeTimer, 30000);
    return () => clearInterval(id);
  }, [savedCallback]);   
  
  const checkLinkStatus = async () => {
    const result = await WondeApi.checkLinkStatus();

    if (!result.error) {
      updateWondeDetails(result);
    }
    
    if (result.error) {
      setLinkStatusLoadingError(true);
    }

    setLinkStatusLoading(false);

    savedCallback.current = function() {
      followUpCheckLinkStatus();
    };
  };

  const followUpCheckLinkStatus = async () => {
    const result = await WondeApi.checkLinkStatus();

    if (!result.error) {
      if(
        !wondeIntegration
        &&
        (wondeAccessDetails.accessRequested !== result.data.bWondeAccessRequested
        ||
        wondeAccessDetails.accessApproved !== result.data.bWondeAccessApproved)
      ){
        updateWondeDetails(result);
      }
    } 
  };

  const updateWondeDetails = (result: any) => {
    let schoolAddressStr = "";
    schoolAddressStr += result.data.addressLine1 ? result.data.addressLine1 + ", " : "";
    schoolAddressStr += result.data.addressLine2 ? result.data.addressLine2 + ", " : "";
    schoolAddressStr += result.data.addressLine3 ? result.data.addressLine3 + ", " : "";
    schoolAddressStr += result.data.townCity ? result.data.townCity + ", " : "";
    schoolAddressStr += result.data.zipPostCode ? result.data.zipPostCode : "";

    setWondeAccessDetails({
      accessRequested: result.data.bWondeAccessRequested,
      accessApproved: result.data.bWondeAccessApproved,
      wondeSchoolId: result.data.wondeSchoolId,
      schoolName: result.data.schoolName,
      schoolAddress: schoolAddressStr,
      schoolUsersEmailAddress: result.data.schoolUsersEmailAddress
    });

    setFindSchoolLoading(false);
    setFindSchoolLoadingError(null);
    setSchoolsList([]);
    setSelectSchoolLoading(false);
    setSelectedSchoolId(null);
    setSelectedSchoolError(null);
  };

  useEffect(() => {
    if(schoolURN && !initEduNumberSet){
      setEducationNumber(schoolURN);
      setInitEduNumberSet(true);
    }
  }, [schoolURN]);  

  const findSchoolsList = async () => {
    setFindSchoolLoading(true);
    setFindSchoolLoadingError("");

    const result = await WondeApi.getSchoolLinkList(educationNumber);

    if (!result.error) {
      if(result.data.length){
        setSchoolsList(result.data);
      } else {
        setFindSchoolLoadingError("There are no schools with this education number.");
      }
    }
    
    if (result.error) {
      setFindSchoolLoadingError(result.error.message);
    }

    setFindSchoolLoading(false);
  };

  const generateSchoolAddress = (school_address: wondeENSchoolAddress) => {
    let schoolAddressStr = "";
    schoolAddressStr += school_address.address_line_1 ? school_address.address_line_1 + ", " : "";
    schoolAddressStr += school_address.address_postcode ? school_address.address_postcode + ", " : "";
    schoolAddressStr += school_address.address_country && school_address.address_country.name ? school_address.address_country.name : "";

    return schoolAddressStr;
  };

  const confirmSchoolSelection = async () => {
    setSelectSchoolLoading(true);
    setSelectedSchoolError("");

    if(!selectedSchoolId){
      setSelectedSchoolError("Please select a school.");
    } else {
      const result = await WondeApi.requestAccessFromSelectedSchool(selectedSchoolId, wondeAccessDetails.schoolUsersEmailAddress);
  
      if (!result.error) {
        followUpCheckLinkStatus();
      }
      
      if (result.error) {
        setSelectedSchoolError(result.error.message);
      }
    }

    setSelectSchoolLoading(false);
  };

  const sendAnotherRequest = () => {
    setWondeAccessDetails({
      accessRequested: false,
      accessApproved: false,
      wondeSchoolId: null,
      schoolName: null,
      schoolAddress: null,
      schoolUsersEmailAddress: null,
    });
  };

  const submitSelectedSyncType = async () => {
    setLinkStatusLoading(true);

    const result = await WondeApi.syncWondeData(wondeAccessDetails.wondeSchoolId, "ClassAndPupil");

    if (!result.error) {
      document.body.style.overflowY = '';
      window.scrollTo(0, 0);
      setWondeSchoolId(wondeAccessDetails.wondeSchoolId);
      setWondeIntegration(true);
      setSyncResponse(result.data);
    }
    
    if (result.error) {
      setSyncLoadingError(
        hasMessageCode(result.error.formErrors, MFA_REQUIRED_CODE)
          ? 'Please verify your identity to sync your Wonde data.'
          : 'An error occurred whilst syncing, please try again.'
      );
      setSyncResponse(null);
      setLinkStatusLoading(false);
      window.scrollTo(0, document.body.scrollHeight);
    }
  };  


  return (
    <div className="WondeLinkPanel">
      {linkStatusLoading && 
        <div className="LoadingLinkStatus">
          <img src={WondeImg} alt="Wonde Logo" className="WondeLogo" />
          <div className="LoadingLabel">Loading...</div>
          <img src={ProgressIndicator} alt="Loading Wonde link status" className="LoadingIndicator" />
        </div>
      }

      {!linkStatusLoading && linkStatusLoadingError &&
        <div className="ErrorLinkStatus">
          <img src={WondeImg} alt="Wonde Logo" className="WondeLogo" />
          <div>An error occurred loading your Wonde details. Please try again later.</div>
        </div>
      }

      {!linkStatusLoading && syncLoadingError && 
        <div className="ErrorLinkStatus SyncError">
          <div>An error occurred whilst syncing. Please try again later.</div>
        </div>
      }

      {!linkStatusLoading && !linkStatusLoadingError && 
        <>
          <div className="PanelInfo">
            <div className="Cell First">
              <img src={WondeImg} alt="Wonde Logo" className="WondeLogo" />

              {!wondeAccessDetails.accessRequested && !wondeAccessDetails.accessApproved && 
                <div className="InfoTxt">
                  <p>If your school uses Wonde, connect your MIS to Get Set 4 Education to easily sync your class and pupil data.</p>
                  <p>Once you have linked your Wonde and Get Set 4 Education accounts, we will request access to the below information. You can select which information you wish to provide via your Wonde account and change these access rights at any time:</p>
                  <ul className="WondePointsList">
                    <li>Class names, key stage and year groups</li>
                    <li>Pupil name, class, year group, demographics and photo</li>
                  </ul>
                  <p>If you have profile photos they will be synced but this will be done overnight.</p>
                  <p>You will need to resync your data at the start of each academic year or if you would like to update your current pupil list.</p>
                </div>
              }

              {wondeAccessDetails.accessRequested && !wondeAccessDetails.accessApproved && 
                <div className="InfoTxt">
                  <p>Sign in to your <a href="https://edu.wonde.com/login" target="_blank" rel="noreferrer">Wonde account</a> to approve your Get Set 4 Education request. Once approved, Wonde may take up to four hours to authorise the connection. Once authorised, this page will say 'Sync now'.</p>
                </div>
              }

              {wondeAccessDetails.accessRequested && wondeAccessDetails.accessApproved && 
                <>
                  <h2>Woohoo!</h2>
                  <div className="InfoTxt NoLineSpacing">
                    <p className="BoldLine">Well done on approving your link between Wonde and Get Set 4 Education.</p>
                    <p>We can now access your chosen data.</p>
                  </div>
                </>
              }

              {wondeApprovalGuide &&
                <a 
                  className="Btn BtnWhiteGrey WondeIntructionsBtn"
                  href={wondeApprovalGuide}
                  target="_blank" rel="noreferrer"
                >
                  <span>View Wonde Approval Guide</span>
                </a>
              }              
            </div>
            <div className="Cell Second">
              {wondeAccessDetails.accessRequested && wondeAccessDetails.accessApproved &&
                <>
                  <div className="SchoolApprovedPanel">
                    <div className="Inner">
                      <div className="TickIcon">
                        <img src={BlueTick} alt="Link approved" />
                      </div>
                      <div className="SchoolDetails">
                        <div className="SchoolName">{wondeAccessDetails.schoolName ? wondeAccessDetails.schoolName : ""}</div>
                        <div className="SchoolAddress">{wondeAccessDetails.schoolAddress ? wondeAccessDetails.schoolAddress : ""}</div>
                      </div>
                    </div>
                  </div>

                  <div className="SchoolApprovedDetailsPanel">
                    <h3>Sync data between Get set 4 education and wonde</h3>
                    <div className="Btn BtnDarkBlue FindSchoolBtn" onClick={() => {submitSelectedSyncType()}}>
                      <span>Sync now</span>
                    </div> 
                  </div>
                </>
              }

              {wondeAccessDetails.accessRequested && !wondeAccessDetails.accessApproved &&
                <>
                  <div className="SchoolPendingPanel">
                    <div className="Txt">{(wondeAccessDetails.schoolName ? wondeAccessDetails.schoolName : "") + " - Pending"}</div>
                  </div>

                  <div className="LineSpacer3"></div>
                  <p>It can take Wonde up to four hours to send us your data once you have approved the permissions in Wonde. The pending button will say 'Sync Now' when ready.</p>

                  <div className="Btn BtnDarkBlue FindSchoolBtn" onClick={() => {sendAnotherRequest()}}>
                    <span>Send another request</span>
                  </div>                  
                </>
              }

              {!wondeAccessDetails.accessRequested && !wondeAccessDetails.accessApproved &&
                <>
                  <div className={"FindSchoolForm" + (selectSchoolLoading ? " Disable" : "")}>
                    <div className="ErrorMsg">{findSchoolLoadingError ? <span>{findSchoolLoadingError}</span> : ""}</div>
                    <div className="FormBox">
                      <div className="FormLabel">
                        <label htmlFor="Education_Number">URN</label>
                      </div>
                      <div className="FormField">
                        <input
                          id="Education_Number"
                          name="Education_Number"
                          className="EditBox"
                          disabled={findSchoolLoading}
                          value={educationNumber}
                          onChange={(e) => {
                            setEducationNumber(e.target.value);
                          }}
                        />
                      </div>
                    </div>

                    <div className={"Btn BtnDarkBlue FindSchoolBtn" + (findSchoolLoading ? " Loading" : "")} onClick={() => {findSchoolsList()}}>
                      {!findSchoolLoading && <span>Find my school</span>}
                      {findSchoolLoading && <img src={ProgressIndicator} alt="Loading schools list" className="LoadSchoolsIndicator" />}
                    </div>
                  </div>

                  {schoolsList && schoolsList.length > 0 &&
                    <>
                      <div className={"SchoolSelectionList" + (selectSchoolLoading ? " Disable" : "")}>
                        <div className="Hdr">Select School</div>
                        <div className="ErrorMsg">{selectedSchoolError ? <span>{selectedSchoolError}</span> : ""}</div>

                        <div className="SchoolOptionsList">
                          {schoolsList.map((item: wondeENSchoolsList, index: number) => {
                            const schoolAddressStr = generateSchoolAddress(item.address);

                            return (
                              <div className={"SchoolOption" + (item.id === selectedSchoolId ? " Active" : "")} onClick={() => {setSelectedSchoolId(item.id)}}>
                                <div className="Inner">
                                  <div className="RadioBtn">
                                    <div className="Radio">
                                      <input id={"Wonde_Select_School_" + index} type="radio" name="Wonde_Select_School" value={item.id} />
                                      <label className="CustomInput" htmlFor="Wonde_Select_School_1"><span className="Txt">School Option {index}</span></label>
                                    </div>                        
                                  </div>
                                  <div className="SchoolsDetails">
                                    <div className="SchoolName">{item.name}</div>
                                    <div className="SchoolAddress">{schoolAddressStr}</div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>                    
                      </div>

                      <div className="LineSpacer3"></div>
                      <p>Can't find your school? Contact <a href="mailto:admin@getset4education.co.uk">admin@getset4education.co.uk</a> for support.</p>

                      <div className={"Btn BtnDarkBlue FindSchoolBtn" + (selectSchoolLoading ? " Loading" : "")} onClick={() => {confirmSchoolSelection()}}>
                        {!selectSchoolLoading && <span>Confirm</span>}
                        {selectSchoolLoading && <img src={ProgressIndicator} alt="Confirming school selection" className="LoadSchoolsIndicator" />}
                      </div>
                    </>
                  }               
                </>
              }
            </div>
          </div>
        </>
      }           
    </div>
  );
};